/**
 * Step 1: Database Provisioning Component
 * Allows users to provision a KnowledgeFlowDB database with X-402 payment
 */

import { useState } from 'react';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useAccount, useSignTypedData, useSwitchChain } from 'wagmi';
import { Database, Loader2, Copy, CheckCircle, AlertCircle } from 'lucide-react';
import type { TenantCredentials, ProvisionDatabaseRequest } from '@/types/database';

// USDC Contract addresses per network (for EIP-3409 TransferWithAuthorization)
const USDC_ADDRESSES: Record<number, string> = {
  8453: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',   // Base mainnet
  84532: '0x036CbD53842c5426634e7929541eC2318f3dCF7e',  // Base Sepolia testnet
  1: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',     // Ethereum mainnet
  11155111: '0x1c7D4B196Cb0C7B01d743Fbc6116a902379C7238', // Sepolia testnet
};

// Provisioning costs (in USDC, with 6 decimals)
const TIER_COSTS = {
  'free-private': 5.0, // $5 USDC on Base Sepolia
  'free-shared': 0, // Free shared tier (not available)
  'pro': 10.0 // $10 USDC for pro tier
};

const TIER_INFO = {
  'free-private': {
    name: 'Private Database',
    description: 'Isolated database with 1M nodes, 10M edges, 10GB storage',
    quota: {
      max_nodes: 1_000_000,
      max_edges: 10_000_000,
      max_storage_mb: 10_000,
      max_queries_per_day: 14_400
    }
  },
  'pro': {
    name: 'Pro',
    description: 'High-performance isolated database with enhanced limits',
    quota: {
      max_nodes: 100_000_000,
      max_edges: 1_000_000_000,
      max_storage_mb: 100_000,
      max_queries_per_day: 1_000_000
    }
  }
};

interface ProvisionDatabaseStepProps {
  onComplete: (credentials: TenantCredentials) => void;
}

export function ProvisionDatabaseStep({ onComplete }: ProvisionDatabaseStepProps) {
  const { address, isConnected, chain } = useAccount();
  const { signTypedDataAsync } = useSignTypedData();
  const { switchChainAsync } = useSwitchChain();

  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [selectedTier, setSelectedTier] = useState<'free-private' | 'pro'>('free-private');
  const [existingApiKey, setExistingApiKey] = useState('');
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<TenantCredentials | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleUseExistingKey = async () => {
    if (!existingApiKey.trim()) {
      setError('Please enter an API key');
      return;
    }

    setIsProvisioning(true);
    setError(null);

    try {
      const agentServiceUrl = import.meta.env.VITE_AGENT_SERVICE_URL || 'http://localhost:5000';

      // Validate API key by fetching stats
      const response = await fetch(`${agentServiceUrl}/api/v1/stats`, {
        headers: {
          'Authorization': `Bearer ${existingApiKey.trim()}`
        }
      });

      if (!response.ok) {
        throw new Error('Invalid API key or database not found');
      }

      // Fetch tenant info to get full credentials
      const tenantResponse = await fetch(`${agentServiceUrl}/api/v1/tenant/info`, {
        headers: {
          'Authorization': `Bearer ${existingApiKey.trim()}`
        }
      });

      if (!tenantResponse.ok) {
        throw new Error('Failed to fetch tenant information');
      }

      const tenantData = await tenantResponse.json();

      // Map to TenantCredentials format
      const creds: TenantCredentials = {
        tenant_id: tenantData.tenant_id,
        email: address || '',
        api_key: existingApiKey.trim(),
        api_base: agentServiceUrl,
        keyspace_name: tenantData.keyspace_name || `tenant_${tenantData.tenant_id}`,
        tier: tenantData.tier || 'free-private',
        database_mode: tenantData.database_mode || 'dedicated',
        quota: tenantData.quota || {
          max_nodes: 1_000_000,
          max_edges: 10_000_000,
          max_storage_mb: 10_000,
          max_queries_per_day: 14_400
        }
      };

      setCredentials(creds);
      localStorage.setItem('kg_tenant_credentials', JSON.stringify(creds));
      onComplete(creds);

    } catch (err: any) {
      console.error('[API Key Validation Error]', err);
      setError(err.message || 'Failed to validate API key');
    } finally {
      setIsProvisioning(false);
    }
  };

  const handleProvision = async () => {
    if (!isConnected || !address) {
      setError('Please connect your wallet first');
      return;
    }

    setIsProvisioning(true);
    setError(null);

    try {
      const cost = TIER_COSTS[selectedTier];
      const agentServiceUrl = import.meta.env.VITE_AGENT_SERVICE_URL || 'http://localhost:5000';

      // Step 1: Request payment requirement from agent
      const prepareResponse = await fetch(`${agentServiceUrl}/api/v1/kg/request-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: {
            messageId: `msg-${Date.now()}`,
            role: 'user',
            parts: [{ kind: 'text', text: `Request access to Knowledge Graph Database. Tier: ${selectedTier}, Wallet: ${address}` }],
            metadata: {}
          }
        })
      });

      if (!prepareResponse.ok) {
        throw new Error(`Failed to prepare payment: ${prepareResponse.status}`);
      }

      const prepareData = await prepareResponse.json();
      const paymentRequest = prepareData.message?.metadata?.['x402.payment.required']?.accepts?.[0];

      if (!paymentRequest && cost > 0) {
        throw new Error('Agent did not return payment requirements');
      }

      // Normalize x402 payment fields (payTo → to, maxAmountRequired → amount)
      const normalizedPayment = paymentRequest ? {
        to: paymentRequest.payTo,
        amount: paymentRequest.maxAmountRequired,
        network: paymentRequest.network,
        asset: paymentRequest.asset,
        extra: paymentRequest.extra
      } : null;

      // Map network name to chain ID (matching working reference)
      const networkToChainId: Record<string, number> = {
        'base': 8453,
        'baseSepolia': 84532,
        'base-sepolia': 84532,
        'ethereum': 1,
        'sepolia': 11155111,
        'arbitrum': 42161,
        'polygon': 137,
      };

      const requiredChainId = normalizedPayment
        ? (networkToChainId[normalizedPayment.network] || 84532)
        : 84532;

      console.log('[Payment] Network:', normalizedPayment?.network, '→ Chain ID:', requiredChainId);

      // Switch to required chain if needed
      if (chain?.id !== requiredChainId) {
        console.log(`[Payment] Switching from chain ${chain?.id} to ${requiredChainId}...`);
        try {
          await switchChainAsync({ chainId: requiredChainId });
          console.log(`[Payment] Switched to chain ${requiredChainId} successfully`);
        } catch (switchErr: any) {
          throw new Error(`Failed to switch network. Please manually switch to the required network in your wallet and try again.`);
        }
      }

      let paymentPayload = undefined;

      // FREE TIER - Skip payment for demo
      // For paid tiers, create EIP-3409 payment authorization
      if (false && cost > 0 && normalizedPayment) {  // Disabled for free tier
        // Step 2: Create EIP-3409 TransferWithAuthorization signature
        const usdcAddress = USDC_ADDRESSES[requiredChainId];
        if (!usdcAddress) {
          throw new Error(`USDC contract not configured for chain ${requiredChainId}`);
        }

        const usdcName = normalizedPayment.extra?.name || 'USD Coin';
        const usdcVersion = normalizedPayment.extra?.version || '2';

        // Generate cryptographically secure nonce (32 bytes)
        const nonceBytes = new Uint8Array(32);
        crypto.getRandomValues(nonceBytes);
        const authNonce = '0x' + Array.from(nonceBytes)
          .map(b => b.toString(16).padStart(2, '0'))
          .join('');

        // Calculate validBefore (10 minutes from now)
        const validBefore = Math.floor(Date.now() / 1000) + 600;

        // Create authorization object for EIP-3409
        const authorization = {
          from: address,
          to: normalizedPayment.to,
          value: normalizedPayment.amount,
          validAfter: '0',
          validBefore: validBefore.toString(),
          nonce: authNonce
        };

        console.log('[Payment] Signing EIP-3409 TransferWithAuthorization...');
        console.log('[Payment] USDC contract:', usdcAddress);
        console.log('[Payment] USDC domain:', { name: usdcName, version: usdcVersion, chainId: requiredChainId });
        console.log('[Payment] Authorization:', authorization);

        // Sign against USDC contract's EIP-712 domain (NOT custom x402 domain!)
        const signature = await signTypedDataAsync({
          domain: {
            name: usdcName,
            version: usdcVersion,
            chainId: requiredChainId,
            verifyingContract: usdcAddress as `0x${string}`
          },
          types: {
            TransferWithAuthorization: [
              { name: 'from', type: 'address' },
              { name: 'to', type: 'address' },
              { name: 'value', type: 'uint256' },
              { name: 'validAfter', type: 'uint256' },
              { name: 'validBefore', type: 'uint256' },
              { name: 'nonce', type: 'bytes32' }
            ]
          },
          primaryType: 'TransferWithAuthorization',
          message: {
            from: address as `0x${string}`,
            to: normalizedPayment.to as `0x${string}`,
            value: BigInt(normalizedPayment.amount),
            validAfter: BigInt(0),
            validBefore: BigInt(validBefore),
            nonce: authNonce as `0x${string}`
          }
        });

        console.log('[Payment] EIP-3409 signature obtained:', signature);

        // Payment payload (nested format for our backend)
        paymentPayload = {
          x402Version: 1,
          scheme: 'exact',
          network: normalizedPayment.network,
          asset: usdcAddress,
          payload: {
            signature,
            authorization
          }
        };
      }

      // Step 3: Call provisioning endpoint with payment
      const messagePayload = {
        message: {
          messageId: `msg-${Date.now()}-${Math.random().toString(36).substring(7)}`,
          role: 'user',
          parts: [
            {
              kind: 'text',
              text: `Request access to Knowledge Graph Database. Tier: ${selectedTier}, Wallet: ${address}`
            }
          ],
          metadata: paymentPayload ? {
            'x402.payment.payload': paymentPayload,
            'x402.payment.status': 'payment-submitted'
          } : undefined
        }
      };

      const response = await fetch(`${agentServiceUrl}/api/v1/kg/request-access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(messagePayload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: { parts: [{ text: response.statusText }] } }));
        const errorText = errorData.message?.parts?.[0]?.text || response.statusText;
        throw new Error(`Provisioning failed: ${errorText}`);
      }

      const result = await response.json();

      // Parse response from x402.agent.card Message format
      const kgCredentials = result.message?.metadata?.kg_credentials;

      if (!kgCredentials) {
        // No payment yet - return payment requirement message
        const paymentMsg = result.message?.parts?.[0]?.text;
        throw new Error(paymentMsg || 'Payment required but no credentials received');
      }

      // Map backend response to TenantCredentials format
      const creds: TenantCredentials = {
        tenant_id: kgCredentials.tenant_id,
        email: address || '',
        api_key: kgCredentials.api_key,
        api_base: kgCredentials.api_endpoint,
        keyspace_name: kgCredentials.keyspace_name,
        tier: kgCredentials.tier,
        database_mode: kgCredentials.database_mode,
        quota: kgCredentials.quota
      };

      setCredentials(creds);

      // Save to localStorage for persistence
      localStorage.setItem('kg_tenant_credentials', JSON.stringify(creds));

      // Notify parent
      onComplete(creds);

    } catch (err: any) {
      console.error('[Provisioning Error]', err);

      // User-friendly error messages (matching working reference)
      let userMessage = 'Failed to provision database';

      if (err instanceof Error) {
        const errMsg = err.message.toLowerCase();

        if (errMsg.includes('user rejected') || errMsg.includes('user denied')) {
          userMessage = 'You rejected the wallet signature. Please try again and approve the transaction.';
        } else if (errMsg.includes('insufficient funds') || errMsg.includes('insufficient balance')) {
          userMessage = `Insufficient USDC balance. Please add at least $${TIER_COSTS[selectedTier]} USDC to your wallet on the required network.`;
        } else if (errMsg.includes('network') || errMsg.includes('switch')) {
          userMessage = err.message; // Already user-friendly from switch error
        } else if (errMsg.includes('payment') || errMsg.includes('402')) {
          userMessage = 'Payment verification failed. Please ensure you have sufficient USDC balance and the correct network.';
        } else if (errMsg.includes('timeout') || errMsg.includes('timed out')) {
          userMessage = 'Request timed out. The service might be busy, please try again in a moment.';
        } else {
          userMessage = `Provisioning failed: ${err.message}`;
        }
      }

      setError(userMessage);
    } finally {
      setIsProvisioning(false);
    }
  };

  const copyToClipboard = async (text: string, field: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm p-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Database className="w-8 h-8 text-purple-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Provision Database</h2>
            <p className="text-gray-600 mt-1">Set up your isolated knowledge graph database</p>
          </div>
        </div>

        {!credentials ? (
          <>
            {/* Mode Toggle */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Database Setup Mode
              </label>
              <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
                <button
                  onClick={() => setMode('new')}
                  className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    mode === 'new'
                      ? 'bg-white text-purple-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Create New Database
                </button>
                <button
                  onClick={() => setMode('existing')}
                  className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    mode === 'existing'
                      ? 'bg-white text-purple-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Use Existing Database
                </button>
              </div>
            </div>

            {mode === 'new' ? (
              <>
                {/* Wallet Connection */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Wallet Connection
                  </label>
                  <ConnectButton />
                  {!isConnected && (
                    <p className="text-sm text-gray-500 mt-2">
                      Connect your wallet to provision a database
                    </p>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Existing API Key Input */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    API Key
                  </label>
                  <input
                    type="password"
                    value={existingApiKey}
                    onChange={(e) => setExistingApiKey(e.target.value)}
                    placeholder="Enter your existing API key"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    Enter the API key from your previously provisioned database
                  </p>
                </div>
              </>
            )}

            {/* Tier Selection */}
            {mode === 'new' && isConnected && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Select Database Tier
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(TIER_INFO).map(([tier, info]) => {
                    const cost = TIER_COSTS[tier as keyof typeof TIER_COSTS];
                    const isSelected = selectedTier === tier;

                    return (
                      <button
                        key={tier}
                        onClick={() => setSelectedTier(tier as 'free-private' | 'pro')}
                        className={`p-4 border-2 rounded-lg text-left transition-all ${
                          isSelected
                            ? 'border-purple-600 bg-purple-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold text-gray-900">{info.name}</h3>
                          <span className="text-sm font-medium text-purple-600">
                            {cost === 0 ? 'Free' : `$${cost} USDC`}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">{info.description}</p>
                        <div className="text-xs text-gray-500 space-y-1">
                          <div>• {info.quota.max_nodes.toLocaleString()} nodes</div>
                          <div>• {info.quota.max_edges.toLocaleString()} edges</div>
                          <div>• {info.quota.max_storage_mb.toLocaleString()} MB storage</div>
                          <div>• {info.quota.max_queries_per_day.toLocaleString()} queries/day</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-900">Provisioning Failed</p>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {mode === 'new' && isConnected && (
              <button
                onClick={handleProvision}
                disabled={isProvisioning}
                className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors font-medium"
              >
                {isProvisioning ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Provisioning Database...
                  </>
                ) : (
                  <>
                    <Database className="w-5 h-5" />
                    Provision Database ({TIER_COSTS[selectedTier] === 0 ? 'Free' : `$${TIER_COSTS[selectedTier]} USDC`})
                  </>
                )}
              </button>
            )}

            {mode === 'existing' && (
              <button
                onClick={handleUseExistingKey}
                disabled={isProvisioning || !existingApiKey.trim()}
                className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors font-medium"
              >
                {isProvisioning ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Validating API Key...
                  </>
                ) : (
                  <>
                    <Database className="w-5 h-5" />
                    Connect to Database
                  </>
                )}
              </button>
            )}
          </>
        ) : (
          /* Success State - Display Credentials */
          <div className="space-y-6">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-green-900">Database Provisioned Successfully!</p>
                <p className="text-sm text-green-700 mt-1">
                  Your isolated knowledge graph database is ready. Save these credentials securely.
                </p>
              </div>
            </div>

            {/* Credentials Display */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tenant ID</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={credentials.tenant_id}
                    readOnly
                    className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm font-mono"
                  />
                  <button
                    onClick={() => copyToClipboard(credentials.tenant_id, 'tenant_id')}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  >
                    {copiedField === 'tenant_id' ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4 text-gray-600" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
                <div className="flex items-center gap-2">
                  <input
                    type="password"
                    value={credentials.api_key}
                    readOnly
                    className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm font-mono"
                  />
                  <button
                    onClick={() => copyToClipboard(credentials.api_key, 'api_key')}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  >
                    {copiedField === 'api_key' ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4 text-gray-600" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">API Base URL</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={credentials.api_base}
                    readOnly
                    className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm"
                  />
                  <button
                    onClick={() => copyToClipboard(credentials.api_base, 'api_base')}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  >
                    {copiedField === 'api_base' ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4 text-gray-600" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Keyspace</label>
                <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm font-mono">
                  {credentials.keyspace_name}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <p className="text-sm text-gray-600">Tier</p>
                  <p className="text-lg font-semibold text-gray-900 capitalize">{credentials.tier}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Database Mode</p>
                  <p className="text-lg font-semibold text-gray-900 capitalize">{credentials.database_mode}</p>
                </div>
              </div>
            </div>

            {/* Curl Examples */}
            <div className="pt-4 border-t border-gray-200">
              <h3 className="text-sm font-medium text-gray-900 mb-3">API Usage Examples</h3>

              <div className="space-y-3">
                {/* Stats Query Example */}
                <div>
                  <p className="text-xs text-gray-600 mb-1">Get Database Statistics:</p>
                  <div className="relative">
                    <pre className="bg-gray-900 text-gray-100 p-3 rounded-md text-xs overflow-x-auto">
{`curl -s '${credentials.api_base}/api/v1/stats' \\
  -H 'Authorization: Bearer ${credentials.api_key}' | jq .`}
                    </pre>
                    <button
                      onClick={() => copyToClipboard(
                        `curl -s '${credentials.api_base}/api/v1/stats' -H 'Authorization: Bearer ${credentials.api_key}' | jq .`,
                        'curl_stats'
                      )}
                      className="absolute top-2 right-2 px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded text-xs transition-colors"
                    >
                      {copiedField === 'curl_stats' ? (
                        <CheckCircle className="w-3 h-3 text-green-400" />
                      ) : (
                        <Copy className="w-3 h-3 text-gray-300" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Query Example */}
                <div>
                  <p className="text-xs text-gray-600 mb-1">Query Agents:</p>
                  <div className="relative">
                    <pre className="bg-gray-900 text-gray-100 p-3 rounded-md text-xs overflow-x-auto">
{`curl -s -X POST '${credentials.api_base}/api/v1/query' \\
  -H 'Content-Type: application/json' \\
  -H 'Authorization: Bearer ${credentials.api_key}' \\
  -d '{"query": "MATCH (a:Agent) RETURN a LIMIT 10"}' | jq .`}
                    </pre>
                    <button
                      onClick={() => copyToClipboard(
                        `curl -s -X POST '${credentials.api_base}/api/v1/query' -H 'Content-Type: application/json' -H 'Authorization: Bearer ${credentials.api_key}' -d '{"query": "MATCH (a:Agent) RETURN a LIMIT 10"}' | jq .`,
                        'curl_query'
                      )}
                      className="absolute top-2 right-2 px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded text-xs transition-colors"
                    >
                      {copiedField === 'curl_query' ? (
                        <CheckCircle className="w-3 h-3 text-green-400" />
                      ) : (
                        <Copy className="w-3 h-3 text-gray-300" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600 mb-3">
                ℹ️ These credentials have been saved to your browser. You can proceed to the next step.
              </p>
              <button
                onClick={() => onComplete(credentials)}
                className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
              >
                Continue to Data Ingestion
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
